import React, {memo, useEffect} from 'react'
import _ from 'lodash'
import {Button, Form, Header, List, Message, Modal} from 'semantic-ui-react'
import {
  withEnvironmentData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withServiceData,
  withVersionData
} from '../../../../hoc'
import {goBack} from '../../../../util'
import {upsertEnvironment} from '../../../../backend'
import {useSafeState} from '../../../../hook'

const isEnvironmentPresent = (existing, name) => {
  return existing.some((e) => e.name.toLowerCase() === name.toLowerCase())
}
const _UpsertEnvironment = (
  {
    service,
    version,
    environment,
    location,
    history,
    navigate,
    mutateVersion,
    mutateEnvironment,
    upsertEnvironment,
    basePath = '/c/services/:serviceId/versions/:versionId/environments'
  }
) => {

  const [state, setState] = useSafeState({
    ...{
      name: '',
      submitted: false,
      displayNameWarning: false,
      errors: {}
    },
    serviceId: service.id,
    versionId: version.id,
    ...(environment || {}),
    environment: (environment || {})
  })

  useEffect(() => {
    setState({
      ...state,
      serviceId: service.id,
      versionId: version.id,
      ...(environment || {}),
      environment: (environment || {})
    })
  }, [service, version, environment])

  const getPathPrefix = () => {
    return basePath
      .replace(':serviceId', service.id)
      .replace(':versionId', version.id)
  }

  const validate = ({name, environment, errors = {}}) => {
    if (!name) {
      errors['environment.name.required'] = 'Name required'
    } else {
      delete errors['environment.name.required']
      if (name.length > 50) {
        errors['environment.name.exceeds.max.length'] = 'Name cannot exceed 50 characters'
      } else {
        delete errors['environment.name.exceeds.max.length']
        if ((environment.name !== name && isEnvironmentPresent(version.environments, name))) {
          errors['environment.taken'] = 'Name already present. Change name and try again'
        } else {
          delete errors['environment.taken']
        }
      }
    }
    return errors
  }

  const onInputChange = (e) => {
    let updatedForm = {...state, ...{[e.target.name]: e.target.value}}
    let errors = {}
    if (state.submitted) {
      errors = validate(updatedForm)
      updatedForm = {...updatedForm, ...{errors}}
    }
    setState(updatedForm)
  }

  const onNameFocus = () => {
    setState({...state, displayNameWarning: !!(environment || {}).id})
  }

  const onClose = () => {
    goBack(history, navigate, getPathPrefix())
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)
    if (_.keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    setState({...state, submitting: true})
    const environment = await upsertEnvironment(state)
    setState({...state, submitting: false})
    mutateVersion()
    mutateEnvironment()
    if (!state.id) {
      navigate(`${getPathPrefix()}/${environment.id}/resources`)
    } else {
      goBack(history, navigate, getPathPrefix())
    }
  }

  const {name = '', errors, submitting} = state
  const editing = !!state.environment.id
  const hasEnvironmentTakenError = _.keys(errors).some((k) => k.indexOf('environment.taken') > -1)
  const hasNameError = _.keys(errors).some((k) => k.indexOf('environment.name') > -1)
  const hasErrors = hasEnvironmentTakenError || hasNameError

  if(!state.environment.name && location.pathname.endsWith('edit')) {
    return null
  }

  return (
    <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose} data-testid='version-upsert-modal'>
      <Header content={`${editing ? 'Edit Environment' : 'Add Environment'}`}/>
      <Modal.Content>
        <Form error={hasErrors}>
          <Form.Field error={hasNameError || hasEnvironmentTakenError} required>
            <label>Name</label>
            <Form.Input placeholder='Enter environment name' name='name'
                        data-testid='environment-name-textbox'
                        value={name}
                        onFocus={onNameFocus}
                        onChange={onInputChange}/>
          </Form.Field>
          <Message hidden={!state.displayNameWarning} color='yellow' size='mini'
                   content="Changing 'name' results in changes to Mock/Docs URL's"/>
          <Message error data-testid='environment-upsert-errors'>
            <Message.Content>
              <List bulleted>
                {
                  _.keys(errors).map((key) => (
                    <List.Item key={key}>{errors[key]}</List.Item>
                  ))
                }
              </List>
            </Message.Content>
          </Message>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} data-testid='environment-upsert-cancel-button'>Cancel</Button>
        <Button primary onClick={onSubmit} data-testid='environment-upsert-submit-button'
                loading={submitting}>{`${editing ? 'Update' : 'Add'}`}</Button>
      </Modal.Actions>
    </Modal>
  )
}

let UpsertEnvironment = (props) => {
  let {handleError} = props
  props = {
    ...props,
    history: window.history,
    upsertEnvironment: handleError(upsertEnvironment)
  }
  return (<_UpsertEnvironment {...props}/>)
}

UpsertEnvironment = withPropsPresent(UpsertEnvironment, ['service', 'version', (props) => !props.versionId || !!props.version])
UpsertEnvironment = withHelmet(UpsertEnvironment, (props) => {
  const {environment} = props
  if (environment && environment.id) {
    return 'Edit Environment'
  }
  return 'Add Environment'
})
UpsertEnvironment = withEnvironmentData(UpsertEnvironment, 'Loading environment...')
UpsertEnvironment = withVersionData(UpsertEnvironment, 'Loading version...')
UpsertEnvironment = withServiceData(UpsertEnvironment, 'Loading service...')
UpsertEnvironment = withErrorHandling(UpsertEnvironment)
UpsertEnvironment = memo(UpsertEnvironment)

export {_UpsertEnvironment, UpsertEnvironment}

export default UpsertEnvironment